import { Spinner } from "@/components/ui/Spinner";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit, Image, Package2, Plus, Search, Trash2 } from "lucide-react";
import React from "react";
import { toast } from "react-hot-toast";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/Card";
import {
  Dropzone,
  DropzoneContent,
  DropzoneEmptyState,
} from "../../components/ui/Dropzone";
import { Input } from "../../components/ui/Input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/Table";
import { fetchWithAuth } from "../../hooks/useAuth";
import { useCloudflareUpload } from "../../hooks/useCloudflareUpload";

interface Supplier {
  id: number;
  name: string;
  description?: string | null;
  imageUrl?: string | null;
  cardImageUrl?: string | null;
  createdAt: string;
}

export function SuppliersPage() {
  return (
    <div className="p-6 space-y-8 bg-background min-h-screen">
      <Card className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Package2 className="w-8 h-8 text-purple-600" />
          <div>
            <h1 className="text-2xl font-bold text-foreground">Furnizori</h1>
          </div>
        </div>
      </Card>

      <SupplierPanel />
    </div>
  );
}

export const SupplierPanel = () => {
  const queryClient = useQueryClient();

  const [search, setSearch] = React.useState("");
  const [isCreating, setIsCreating] = React.useState(false);
  const [editingSupplier, setEditingSupplier] = React.useState<Supplier | null>(
    null
  );
  const [deletingSupplierId, setDeletingSupplierId] = React.useState<
    number | null
  >(null);

  const [formData, setFormData] = React.useState({
    name: "",
    description: "",
    imageUrl: "",
    cardImageUrl: "",
  });

  // Image upload for supplier main image
  const imageUpload = useCloudflareUpload({
    maxFiles: 1,
    allowedMimeTypes: ["image/*"],
  });

  // Image upload for supplier card image
  const cardImageUpload = useCloudflareUpload({
    maxFiles: 1,
    allowedMimeTypes: ["image/*"],
  });

  // Dropzone keys to force remount when clearing images
  const [imageDropzoneKey, setImageDropzoneKey] = React.useState(0);
  const [cardDropzoneKey, setCardDropzoneKey] = React.useState(0);

  // Track last uploaded lengths to only react to new uploads
  const lastImageUploadedLengthRef = React.useRef(0);
  const lastCardUploadedLengthRef = React.useRef(0);

  // List suppliers
  const {
    data: suppliers,
    isLoading,
    refetch,
  } = useQuery<Supplier[]>({
    queryKey: ["suppliers"],
    queryFn: async () => {
      const res = await fetchWithAuth("/api/admin/suppliers");
      if (!res.ok) {
        toast.error("Eroare la încărcarea furnizorilor");
        return [] as Supplier[];
      }
      const json = await res.json();
      if (Array.isArray(json)) return json as Supplier[];
      if (json && Array.isArray(json.data)) return json.data as Supplier[];
      if (json && Array.isArray(json.date)) return json.date as Supplier[];
      return [] as Supplier[];
    },
  });

  const filteredSuppliers = React.useMemo(() => {
    if (!suppliers || !Array.isArray(suppliers)) return [] as Supplier[];
    if (!search.trim()) return suppliers;
    const lower = search.toLowerCase();
    return suppliers.filter((s) => s.name.toLowerCase().includes(lower));
  }, [suppliers, search]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ["suppliers"] });
    await refetch();
  };

  const createSupplier = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await fetchWithAuth("/api/admin/suppliers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: data.name,
          description: data.description || null,
          imageUrl: data.imageUrl || null,
          cardImageUrl: data.cardImageUrl || null,
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la crearea furnizorului");
      }
      return res.json();
    },
    onSuccess: async () => {
      toast.success("Furnizor creat cu succes");
      await invalidate();
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error?.message || "Eroare la crearea furnizorului");
    },
  });

  const updateSupplier = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: Partial<typeof formData>;
    }) => {
      const res = await fetchWithAuth(`/api/admin/suppliers/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...(data.name !== undefined ? { name: data.name } : {}),
          ...(data.description !== undefined
            ? { description: data.description || null }
            : {}),
          ...(data.imageUrl !== undefined
            ? { imageUrl: data.imageUrl || null }
            : {}),
          ...(data.cardImageUrl !== undefined
            ? { cardImageUrl: data.cardImageUrl || null }
            : {}),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la actualizarea furnizorului");
      }
      return res.json();
    },
    onSuccess: async () => {
      toast.success("Furnizor actualizat cu succes");
      await invalidate();
      resetForm();
    },
    onError: (error: any) => {
      toast.error(error?.message || "Eroare la actualizarea furnizorului");
    },
  });

  const deleteSupplier = useMutation({
    mutationFn: async (id: number) => {
      const res = await fetchWithAuth(`/api/admin/suppliers/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Eroare la ștergerea furnizorului");
      }
      return res.json();
    },
    onSuccess: async () => {
      toast.success("Furnizor șters cu succes");
      await invalidate();
    },
    onError: (error: any) => {
      toast.error(error?.message || "Eroare la ștergerea furnizorului");
    },
    onSettled: () => {
      setDeletingSupplierId(null);
    },
  });

  const resetForm = () => {
    setIsCreating(false);
    setEditingSupplier(null);
    setFormData({
      name: "",
      description: "",
      imageUrl: "",
      cardImageUrl: "",
    });
    imageUpload.setFiles([]);
    imageUpload.setErrors([]);
    cardImageUpload.setFiles([]);
    cardImageUpload.setErrors([]);
    setImageDropzoneKey(0);
    setCardDropzoneKey(0);
    lastImageUploadedLengthRef.current = 0;
    lastCardUploadedLengthRef.current = 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // Ensure uploaded image URLs are synced to formData before submission
    let finalFormData = { ...formData };

    if (imageUpload.uploadedResults.length > 0) {
      const latestImage =
        imageUpload.uploadedResults[imageUpload.uploadedResults.length - 1];
      if (latestImage && latestImage.publicUrl) {
        finalFormData.imageUrl = latestImage.publicUrl;
      }
    }

    if (cardImageUpload.uploadedResults.length > 0) {
      const latestCardImage =
        cardImageUpload.uploadedResults[
          cardImageUpload.uploadedResults.length - 1
        ];
      if (latestCardImage && latestCardImage.publicUrl) {
        finalFormData.cardImageUrl = latestCardImage.publicUrl;
      }
    }

    if (editingSupplier) {
      updateSupplier.mutate({
        id: editingSupplier.id,
        data: finalFormData,
      });
    } else {
      createSupplier.mutate(finalFormData);
    }
  };

  const handleEdit = (supplier: Supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      description: supplier.description || "",
      imageUrl: supplier.imageUrl || "",
      cardImageUrl: supplier.cardImageUrl || "",
    });
    setIsCreating(true);
    lastImageUploadedLengthRef.current = 0;
    lastCardUploadedLengthRef.current = 0;
  };

  const handleDelete = (id: number) => {
    if (
      window.confirm(
        "Sigur doriți să ștergeți acest furnizor? Această acțiune nu poate fi anulată."
      )
    ) {
      setDeletingSupplierId(id);
      deleteSupplier.mutate(id);
    }
  };

  // Reset uploader state when opening form for new supplier
  React.useEffect(() => {
    if (isCreating && !editingSupplier) {
      imageUpload.setFiles([]);
      imageUpload.setErrors([]);
      cardImageUpload.setFiles([]);
      cardImageUpload.setErrors([]);
      setFormData((prev) => ({
        ...prev,
        imageUrl: "",
        cardImageUrl: "",
      }));
      lastImageUploadedLengthRef.current = 0;
      lastCardUploadedLengthRef.current = 0;
    }
  }, [isCreating, editingSupplier]);

  // Handle image upload completion
  React.useEffect(() => {
    const currentLength = imageUpload.uploadedResults.length;
    const lastLength = lastImageUploadedLengthRef.current;

    if (currentLength > lastLength && imageUpload.uploadedResults.length > 0) {
      const latest = imageUpload.uploadedResults[currentLength - 1];
      if (latest && latest.publicUrl) {
        setFormData((prev) => ({
          ...prev,
          imageUrl: latest.publicUrl,
        }));
        toast.success("Imagine furnizor încărcată cu succes");
        lastImageUploadedLengthRef.current = currentLength;
      }
    } else if (currentLength !== lastLength) {
      lastImageUploadedLengthRef.current = currentLength;
    }
  }, [imageUpload.uploadedResults]);

  // Handle card image upload completion
  React.useEffect(() => {
    const currentLength = cardImageUpload.uploadedResults.length;
    const lastLength = lastCardUploadedLengthRef.current;

    if (
      currentLength > lastLength &&
      cardImageUpload.uploadedResults.length > 0
    ) {
      const latest = cardImageUpload.uploadedResults[currentLength - 1];
      if (latest && latest.publicUrl) {
        setFormData((prev) => ({
          ...prev,
          cardImageUrl: latest.publicUrl,
        }));
        toast.success("Imagine card furnizor încărcată cu succes");
        lastCardUploadedLengthRef.current = currentLength;
      }
    } else if (currentLength !== lastLength) {
      lastCardUploadedLengthRef.current = currentLength;
    }
  }, [cardImageUpload.uploadedResults]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package2 className="w-5 h-5 text-purple-600" />
            Furnizori
          </div>
          <div className="flex gap-2">
            <Button
              className="flex items-center gap-2"
              onClick={() => setIsCreating(true)}
            >
              <Plus className="w-4 h-4" />
              Adaugă Furnizor
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Supplier Form */}
        {isCreating && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">
                {editingSupplier ? "Editează Furnizor" : "Adaugă Furnizor Nou"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Nume Furnizor
                    </label>
                    <Input
                      value={formData.name}
                      onChange={(e) =>
                        setFormData((f) => ({
                          ...f,
                          name: e.target.value,
                        }))
                      }
                      placeholder="ex: Editura X"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">
                      Descriere (opțional)
                    </label>
                    <Input
                      value={formData.description}
                      onChange={(e) =>
                        setFormData((f) => ({
                          ...f,
                          description: e.target.value,
                        }))
                      }
                      placeholder="ex: Distribuitor oficial pentru..."
                    />
                  </div>
                </div>

                {/* Image Upload Section */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Imagine Furnizor
                  </label>
                  {formData.imageUrl ? (
                    <div className="relative">
                      <img
                        src={formData.imageUrl}
                        alt="Preview"
                        className="w-32 h-32 object-cover rounded-md border"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => {
                          setFormData((f) => ({ ...f, imageUrl: "" }));
                          imageUpload.setFiles([]);
                          imageUpload.setErrors([]);
                          setImageDropzoneKey((prev) => prev + 1);
                          lastImageUploadedLengthRef.current = 0;
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Dropzone
                      {...imageUpload}
                      key={`supplier-image-${
                        editingSupplier?.id || "new"
                      }-${imageDropzoneKey}`}
                    >
                      <DropzoneEmptyState />
                      <DropzoneContent />
                    </Dropzone>
                  )}
                  {imageUpload.files.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        onClick={async () => {
                          await imageUpload.onUpload();
                        }}
                        disabled={imageUpload.loading}
                        size="sm"
                      >
                        {imageUpload.loading
                          ? "Se încarcă..."
                          : "Încarcă imagine"}
                      </Button>
                      {imageUpload.errors.length > 0 && (
                        <div className="text-sm text-red-600">
                          {imageUpload.errors[0].message}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Card Image Upload Section */}
                <div className="space-y-2">
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Image className="w-4 h-4" />
                    Imagine Card Furnizor
                  </label>
                  {formData.cardImageUrl ? (
                    <div className="relative">
                      <img
                        src={formData.cardImageUrl}
                        alt="Preview card"
                        className="w-32 h-32 object-cover rounded-md border"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="absolute top-2 right-2"
                        onClick={() => {
                          setFormData((f) => ({
                            ...f,
                            cardImageUrl: "",
                          }));
                          cardImageUpload.setFiles([]);
                          cardImageUpload.setErrors([]);
                          setCardDropzoneKey((prev) => prev + 1);
                          lastCardUploadedLengthRef.current = 0;
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Dropzone
                      {...cardImageUpload}
                      key={`supplier-card-image-${
                        editingSupplier?.id || "new"
                      }-${cardDropzoneKey}`}
                    >
                      <DropzoneEmptyState />
                      <DropzoneContent />
                    </Dropzone>
                  )}
                  {cardImageUpload.files.length > 0 && (
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        onClick={async () => {
                          await cardImageUpload.onUpload();
                        }}
                        disabled={cardImageUpload.loading}
                        size="sm"
                      >
                        {cardImageUpload.loading
                          ? "Se încarcă..."
                          : "Încarcă imagine card"}
                      </Button>
                      {cardImageUpload.errors.length > 0 && (
                        <div className="text-sm text-red-600">
                          {cardImageUpload.errors[0].message}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={resetForm}>
                    Anulează
                  </Button>
                  <Button
                    type="submit"
                    disabled={
                      createSupplier.isPending ||
                      updateSupplier.isPending ||
                      imageUpload.loading ||
                      cardImageUpload.loading
                    }
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {createSupplier.isPending || updateSupplier.isPending
                      ? "Se salvează..."
                      : editingSupplier
                      ? "Actualizează"
                      : "Salvează"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Supplier Search */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Caută furnizori..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {/* Suppliers List */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="font-semibold">ID</TableHead>
                <TableHead className="font-semibold">Imagine</TableHead>
                <TableHead className="font-semibold">Nume</TableHead>
                <TableHead className="font-semibold">Descriere</TableHead>
                <TableHead className="font-semibold">Imagine Card</TableHead>
                <TableHead className="font-semibold">Data Creării</TableHead>
                <TableHead className="font-semibold">Acțiuni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10">
                    <div className="flex items-center justify-center text-muted-foreground">
                      Se încarcă furnizorii...
                    </div>
                  </TableCell>
                </TableRow>
              ) : filteredSuppliers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10">
                    <div className="text-muted-foreground">
                      {search
                        ? "Nu există furnizori care să se potrivească cu căutarea"
                        : "Nu există furnizori"}
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                filteredSuppliers.map((supplier) => (
                  <TableRow key={supplier.id}>
                    <TableCell className="font-medium">
                      #{supplier.id}
                    </TableCell>
                    <TableCell>
                      {supplier.imageUrl ? (
                        <img
                          src={supplier.imageUrl}
                          alt={supplier.name}
                          className="w-12 h-12 object-cover rounded-md border"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-md border bg-gray-100 flex items-center justify-center">
                          <Image className="w-5 h-5 text-gray-400" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-foreground">
                        {supplier.name}
                      </span>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      {supplier.description ? (
                        <span className="text-sm text-muted-foreground line-clamp-2">
                          {supplier.description}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          Fără descriere
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      {supplier.cardImageUrl ? (
                        <img
                          src={supplier.cardImageUrl}
                          alt={`${supplier.name} card`}
                          className="w-12 h-12 object-cover rounded-md border"
                        />
                      ) : (
                        <Badge variant="outline">Nesetat</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(supplier.createdAt).toLocaleDateString("ro-RO")}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-1"
                          onClick={() => handleEdit(supplier)}
                        >
                          <Edit className="w-4 h-4" />
                          Editează
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(supplier.id)}
                          disabled={
                            deleteSupplier.isPending &&
                            deletingSupplierId === supplier.id
                          }
                        >
                          {deleteSupplier.isPending &&
                          deletingSupplierId === supplier.id ? (
                            <>
                              <Spinner className="mr-2" /> Ștergere...
                            </>
                          ) : (
                            <>
                              <Trash2 className="w-4 h-4" /> Șterge
                            </>
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
